<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60" @click.self="$emit('close')">
    <div class="bg-[#0a0a0f] rounded-xl sm:rounded-2xl shadow-2xl w-full h-full max-w-[100vw] sm:max-w-[98vw] max-h-[100vh] sm:max-h-[98vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <header class="px-4 py-3 bg-black/40 border-b border-orange-500/20 flex items-center justify-between backdrop-blur-sm">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center text-xl shadow-lg shadow-orange-500/40">
            🔧
          </div>
          <div>
            <div class="text-white text-lg font-bold">Hotjet</div>
            <div class="text-gray-500 text-xs uppercase tracking-widest">Servisní technici</div>
          </div>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <!-- Main content -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Sidebar -->
        <aside class="w-[420px] bg-[#101018] border-r border-[#2a2a3a] flex flex-col overflow-hidden">
          <div class="flex-1 overflow-y-auto p-4">
            <!-- Search section -->
            <div class="bg-[#1a1a25] rounded-xl p-3.5 mb-4">
              <div class="relative w-full">
                <input
                  v-model="addressInput"
                  type="text"
                  placeholder="📍 Zadejte adresu instalace..."
                  autocomplete="off"
                  class="w-full px-3.5 py-3 pr-10 border border-[#2a2a3a] rounded-lg bg-[#0a0a0f] text-white text-sm mb-2.5 focus:outline-none focus:border-orange-500"
                  @keypress.enter="searchByAddress"
                />
                <button
                  v-if="addressInput"
                  @click="clearSearch"
                  class="absolute right-2 top-1/2 -translate-y-[calc(50%+5px)] w-6 h-6 bg-red-500/80 hover:bg-red-500 text-white rounded-full text-sm font-bold flex items-center justify-center transition-colors"
                  title="Zrušit vyhledávání"
                >✕</button>
              </div>
              <button
                @click="searchByAddress"
                :disabled="isSearching"
                class="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 border-none rounded-lg text-white font-semibold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                🔍 Najít nejbližší techniky
              </button>
              <div v-if="searchStatus" class="mt-2.5 p-2.5 rounded-lg text-sm flex items-center gap-2.5" :class="searchStatusClass">
                <div v-if="isSearching" class="w-4 h-4 border-2 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
                <span>{{ searchStatusText }}</span>
              </div>
            </div>

            <!-- Filter section -->
            <div class="mb-4">
              <input
                v-model="filterInput"
                type="text"
                placeholder="🔍 Filtrovat techniky..."
                class="w-full px-3.5 py-2.5 border border-[#2a2a3a] rounded-lg bg-[#1a1a25] text-white text-sm mb-3 focus:outline-none focus:border-orange-500"
              />
              <div class="bg-[#1a1a25] rounded-lg p-3">
                <div class="flex justify-between items-center mb-2.5 cursor-pointer" @click="toggleAllRegions">
                  <span class="text-xs font-semibold text-gray-500">🗺️ Filtrovat podle kraje</span>
                  <span class="text-xs text-orange-500 hover:underline">{{ allRegionsSelected ? 'Zrušit vše' : 'Vybrat vše' }}</span>
                </div>
                <div class="grid grid-cols-2 gap-1">
                  <label
                    v-for="region in czechRegions"
                    :key="region.id"
                    class="region-filter-item flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-white/5 text-xs"
                    :class="{ 'opacity-40 cursor-default hover:bg-transparent': region.count === 0 }"
                    :style="{ color: region.color }"
                    @mouseenter="highlightRegion(region.id)"
                    @mouseleave="unhighlightRegion"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedRegions.has(region.id)"
                      :disabled="region.count === 0"
                      class="hidden"
                      @change="toggleRegionFilter(region.id)"
                    />
                    <span
                      class="w-3.5 h-3.5 border-2 rounded flex items-center justify-center flex-shrink-0"
                      :style="{ borderColor: region.color, backgroundColor: selectedRegions.has(region.id) ? region.color : 'transparent' }"
                    >
                      <span v-if="selectedRegions.has(region.id)" class="text-[10px] text-[#0a0a0f]">✓</span>
                    </span>
                    <span class="flex-1 text-white whitespace-nowrap overflow-hidden text-ellipsis">{{ region.name }}</span>
                    <span class="text-[9px] font-medium px-1.5 py-0.5 rounded-lg bg-white/10 whitespace-nowrap" :style="{ color: region.count === 0 ? 'rgba(255,255,255,0.3)' : region.color }">
                      {{ formatTechCount(region.count) }}
                    </span>
                  </label>
                  <!-- Slovakia separator -->
                  <div class="col-span-2 h-px bg-white/20 my-2"></div>
                  <label
                    class="region-filter-item flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-white/5 text-xs"
                    :class="{ 'opacity-40 cursor-default hover:bg-transparent': slovakiaRegion.count === 0 }"
                    :style="{ color: slovakiaRegion.color }"
                    @mouseenter="highlightRegion('slovensko')"
                    @mouseleave="unhighlightRegion"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedRegions.has('slovensko')"
                      :disabled="slovakiaRegion.count === 0"
                      class="hidden"
                      @change="toggleRegionFilter('slovensko')"
                    />
                    <span
                      class="w-3.5 h-3.5 border-2 rounded flex items-center justify-center flex-shrink-0"
                      :style="{ borderColor: slovakiaRegion.color, backgroundColor: selectedRegions.has('slovensko') ? slovakiaRegion.color : 'transparent' }"
                    >
                      <span v-if="selectedRegions.has('slovensko')" class="text-[10px] text-[#0a0a0f]">✓</span>
                    </span>
                    <span class="flex-1 text-white whitespace-nowrap overflow-hidden text-ellipsis">{{ slovakiaRegion.name }}</span>
                    <span class="text-[9px] font-medium px-1.5 py-0.5 rounded-lg bg-white/10 whitespace-nowrap" :style="{ color: slovakiaRegion.count === 0 ? 'rgba(255,255,255,0.3)' : slovakiaRegion.color }">
                      {{ formatTechCount(slovakiaRegion.count) }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- List header -->
            <div class="flex justify-between items-center mb-3 px-1">
              <span class="text-sm font-semibold text-gray-500">{{ isSearchMode ? '📍 Nejbližší technici' : 'Všichni technici' }}</span>
              <span class="text-sm text-orange-500 font-medium">{{ formatTechCount(filteredTechnicians.length) }}</span>
            </div>

            <!-- Technician list -->
            <div class="flex flex-col gap-2">
              <div
                v-for="(tech, index) in sortedTechnicians"
                :key="tech.name"
                class="tech-card bg-[#1a1a25] rounded-lg p-3 border-l-4 cursor-pointer transition-all hover:bg-white/5 hover:translate-x-0.5"
                :class="{ 'bg-orange-500/15 ring-1 ring-orange-500': highlightedTech === tech.name }"
                :style="{ borderLeftColor: regionColors[tech.regionId] || '#888' }"
                @mouseenter="highlightTech(tech.name, tech.regionId)"
                @mouseleave="unhighlightRegion"
                @click="focusTech(index)"
              >
                <div class="flex justify-between items-start mb-1.5">
                  <span class="font-semibold text-sm text-white">{{ tech.name }}</span>
                  <span class="text-[10px] px-2 py-0.5 rounded-xl font-medium" :style="{ background: regionColors[tech.regionId] + '22', color: regionColors[tech.regionId] }">
                    {{ tech.region }}
                  </span>
                </div>
                <div class="text-xs text-gray-500 mt-0.5">📍 {{ tech.city }}</div>
                <div class="text-xs text-gray-500 mt-0.5">📞 {{ tech.phone }}</div>
                <div class="text-xs text-gray-500 mt-0.5">✉️ {{ tech.email }}</div>
                <div v-if="tech.distance !== undefined" class="text-xs text-green-500 font-semibold mt-1.5">
                  🚗 {{ tech.distance.toFixed(1) }} km{{ tech.duration ? ` • ⏱️ ${formatDuration(tech.duration)}` : '' }}
                </div>
              </div>
            </div>
          </div>
        </aside>

        <!-- Map container -->
        <div class="flex-1 relative">
          <div v-if="!isSearchMode" class="absolute bottom-5 left-1/2 -translate-x-1/2 bg-black/80 px-5 py-2.5 rounded-full text-xs text-gray-500 z-[1000] pointer-events-none">
            💡 Zadejte adresu pro nalezení nejbližších techniků
          </div>
          <div ref="mapContainer" class="w-full h-full"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

// Refs
const mapContainer = ref(null);
const addressInput = ref('');
const filterInput = ref('');
const searchStatus = ref(null); // null, 'loading', 'success', 'error'
const searchStatusText = ref('');
const isSearching = ref(false);
const isSearchMode = ref(false);
const currentLocation = ref(null);
const selectedRegions = ref(new Set());
const highlightedTech = ref(null);
const highlightedRegionId = ref(null);

// Map instance
let map = null;
let markers = [];
let routeLines = [];
let regionPolygons = {};

// Constants - use proxy endpoints to avoid CORS issues
const MAPY_API_KEY = import.meta.env.VITE_MAPY_API_KEY; // Used for map tiles

const technicians = ref([
  { name: "Matis Miroslav", city: "Šenov", region: "Moravskoslezský kraj", regionId: "moravskoslezsky", phone: "608767458", email: "mirekmatis@gmail.com", lat: 49.7833, lon: 18.3833 },
  { name: "Majkus Zdeněk", city: "Ostrava", region: "Moravskoslezský kraj", regionId: "moravskoslezsky", phone: "737722225", email: "zdenek.majkus@volny.cz", lat: 49.8209, lon: 18.2625 },
  { name: "Michal Tisoň", city: "Bruntál", region: "Moravskoslezský kraj", regionId: "moravskoslezsky", phone: "603319503", email: "fajnklima@email.cz", lat: 49.9886, lon: 17.4647 },
  { name: "Janíček Rostislav", city: "Šumperk", region: "Olomoucký kraj", regionId: "olomoucky", phone: "604848616", email: "info@eltop-janicek.cz", lat: 49.9656, lon: 16.9706 },
  { name: "Lachnit Petr", city: "Prostějov", region: "Olomoucký kraj", regionId: "olomoucky", phone: "775045295", email: "cztc.servis@gmail.com", lat: 49.4722, lon: 17.1119 },
  { name: "Tomek Marek", city: "Třebíč", region: "Kraj Vysočina", regionId: "vysocina", phone: "777332189", email: "info@tomek-cerpadla.cz", lat: 49.2147, lon: 15.8817 },
  { name: "Kužel Jakub", city: "Malá Paseka", region: "Středočeský kraj", regionId: "stredocesky", phone: "608771677", email: "info@elcoolcz.cz", lat: 49.9500, lon: 14.4500 },
  { name: "Kahoun Václav", city: "Čechtice", region: "Středočeský kraj", regionId: "stredocesky", phone: "608969116", email: "vaclav@tzb-kahoun.cz", lat: 49.6167, lon: 15.0667 },
  { name: "Kalenský Jindřich", city: "Vodňany", region: "Jihočeský kraj", regionId: "jihocesky", phone: "608417187", email: "jindrichkalensky@seznam.cz", lat: 49.1478, lon: 14.1747 },
  { name: "Iška Martin", city: "Křemže", region: "Jihočeský kraj", regionId: "jihocesky", phone: "778000223", email: "iskamartin@gmail.com", lat: 48.9067, lon: 14.3069 },
  { name: "Procházka Petr", city: "Tuřany", region: "Karlovarský kraj", regionId: "karlovarsky", phone: "604684841", email: "topenartsvi.prochazka@seznam.cz", lat: 50.0833, lon: 12.7333 },
  { name: "Hrdina Jaroslav", city: "Františkovy Lázně", region: "Karlovarský kraj", regionId: "karlovarsky", phone: "736612101", email: "hrdina@fons-fl.cz", lat: 50.1203, lon: 12.3517 },
  { name: "Rezek David", city: "Pomezí nad Ohří", region: "Karlovarský kraj", regionId: "karlovarsky", phone: "603482261", email: "rezek.david@icloud.com", lat: 50.2500, lon: 12.3000 },
  { name: "Zimanzl B.", city: "Plzeň", region: "Plzeňský kraj", regionId: "plzensky", phone: "724053630", email: "oes@email.cz", lat: 49.7384, lon: 13.3736 },
  { name: "Kortán Jaroslav", city: "Dvůr Králové nad Labem", region: "Královéhradecký kraj", regionId: "kralovehradecky", phone: "603518540", email: "erkona@seznam.cz", lat: 50.4319, lon: 15.8142 },
  { name: "Kortán Ml.", city: "Dvůr Králové nad Labem", region: "Královéhradecký kraj", regionId: "kralovehradecky", phone: "603193300", email: "linkotrio@seznam.cz", lat: 50.4319, lon: 15.8142 },
  { name: "Martiš Patrik", city: "Kysucké Nové Mesto", region: "Slovensko", regionId: "slovensko", phone: "+421949391109", email: "patrikmartis80@gmail.com", lat: 49.2994, lon: 18.7869 },
  { name: "Junger Milan", city: "Ivanka pri Dunaji", region: "Slovensko", regionId: "slovensko", phone: "+421904176837", email: "junger@grandplus.sk", lat: 48.1833, lon: 17.2667 },
  { name: "Sekan", city: "Ružomberok", region: "Slovensko", regionId: "slovensko", phone: "+421905669325", email: "martin.hlad@infonetcentrum.sk", lat: 49.0761, lon: 19.3064 },
  { name: "Šunderlík Radovan", city: "Ivanka pri Dunaji", region: "Slovensko", regionId: "slovensko", phone: "+421904701373", email: "sunderlik@msdcompany.sk", lat: 48.1833, lon: 17.2667 }
]);

const regionColors = {
  moravskoslezsky: '#3b82f6',
  olomoucky: '#8b5cf6',
  vysocina: '#ec4899',
  stredocesky: '#10b981',
  jihocesky: '#22c55e',
  karlovarsky: '#f59e0b',
  plzensky: '#f97316',
  kralovehradecky: '#06b6d4',
  slovensko: '#ffffff',
  pardubicky: '#14b8a6',
  liberecky: '#a855f7',
  ustecky: '#eab308',
  jihomoravsky: '#ef4444',
  zlinsky: '#84cc16',
  praha: '#f43f5e'
};

const regionNames = {
  moravskoslezsky: 'Moravskoslezský',
  olomoucky: 'Olomoucký',
  vysocina: 'Vysočina',
  stredocesky: 'Středočeský',
  jihocesky: 'Jihočeský',
  karlovarsky: 'Karlovarský',
  plzensky: 'Plzeňský',
  kralovehradecky: 'Královéhradecký',
  slovensko: 'Slovensko',
  pardubicky: 'Pardubický',
  liberecky: 'Liberecký',
  ustecky: 'Ústecký',
  jihomoravsky: 'Jihomoravský',
  zlinsky: 'Zlínský',
  praha: 'Praha'
};

// Computed
const regionCounts = computed(() => {
  const counts = {};
  technicians.value.forEach(t => {
    counts[t.regionId] = (counts[t.regionId] || 0) + 1;
  });
  return counts;
});

const czechRegions = computed(() => {
  const allRegionIds = Object.keys(regionNames).filter(id => id !== 'slovensko');
  return allRegionIds
    .map(id => ({
      id,
      name: regionNames[id],
      color: regionColors[id] || '#888',
      count: regionCounts.value[id] || 0
    }))
    .sort((a, b) => {
      const countDiff = b.count - a.count;
      if (countDiff !== 0) return countDiff;
      return a.name.localeCompare(b.name, 'cs');
    });
});

const slovakiaRegion = computed(() => ({
  id: 'slovensko',
  name: regionNames.slovensko,
  color: regionColors.slovensko,
  count: regionCounts.value.slovensko || 0
}));

const allRegionsSelected = computed(() => {
  const allRegions = [...new Set(technicians.value.map(t => t.regionId))];
  return selectedRegions.value.size === allRegions.length;
});

const filteredTechnicians = computed(() => {
  const query = filterInput.value.toLowerCase();
  let filtered = technicians.value;

  // Filter by selected regions
  const allRegions = [...new Set(technicians.value.map(t => t.regionId))];
  if (selectedRegions.value.size < allRegions.length) {
    filtered = filtered.filter(t => selectedRegions.value.has(t.regionId));
  }

  // Filter by search query
  if (query) {
    filtered = filtered.filter(t =>
      t.name.toLowerCase().includes(query) ||
      t.city.toLowerCase().includes(query) ||
      t.region.toLowerCase().includes(query)
    );
  }

  return filtered;
});

const sortedTechnicians = computed(() => {
  if (isSearchMode.value && currentLocation.value) {
    return [...filteredTechnicians.value].sort((a, b) => (a.distance || 9999) - (b.distance || 9999));
  }
  return filteredTechnicians.value;
});

const searchStatusClass = computed(() => {
  switch (searchStatus.value) {
    case 'loading': return 'bg-orange-500/10 text-orange-500';
    case 'success': return 'bg-green-500/10 text-green-500';
    case 'error': return 'bg-red-500/10 text-red-500';
    default: return '';
  }
});

// Methods
function formatTechCount(count) {
  if (count === 0) return '0 techniků';
  if (count === 1) return '1 technik';
  if (count <= 4) return count + ' technici';
  return count + ' techniků';
}

function formatDuration(minutes) {
  if (minutes < 60) return Math.round(minutes) + ' min';
  return Math.floor(minutes / 60) + 'h ' + Math.round(minutes % 60) + 'min';
}

function initSelectedRegions() {
  const allRegionIds = Object.keys(regionNames);
  allRegionIds.forEach(regionId => {
    if (regionCounts.value[regionId] > 0) {
      selectedRegions.value.add(regionId);
    }
  });
}

function toggleRegionFilter(regionId) {
  if (selectedRegions.value.has(regionId)) {
    selectedRegions.value.delete(regionId);
  } else {
    selectedRegions.value.add(regionId);
  }
  // Force reactivity
  selectedRegions.value = new Set(selectedRegions.value);
  updateMapMarkers();
}

function toggleAllRegions() {
  const allRegions = [...new Set(technicians.value.map(t => t.regionId))];
  if (selectedRegions.value.size === allRegions.length) {
    selectedRegions.value.clear();
  } else {
    allRegions.forEach(r => selectedRegions.value.add(r));
  }
  selectedRegions.value = new Set(selectedRegions.value);
  updateMapMarkers();
}

function highlightTech(name, regionId) {
  highlightedTech.value = name;
  highlightRegion(regionId);

  // Open popup for matching markers
  markers.forEach(m => {
    if (m.getPopup && m.getPopup() && m.getPopup().getContent().includes(name)) {
      m.openPopup();
    }
  });
}

function highlightRegion(regionId) {
  highlightedRegionId.value = regionId;

  Object.entries(regionPolygons).forEach(([id, polygon]) => {
    if (id === regionId) {
      polygon.setStyle({ fillOpacity: 0.45, weight: 3, opacity: 1 });
      polygon.bringToFront();
    } else {
      polygon.setStyle({ fillOpacity: 0.1, weight: 1, opacity: 0.4 });
    }
  });
}

function unhighlightRegion() {
  highlightedTech.value = null;
  highlightedRegionId.value = null;

  Object.values(regionPolygons).forEach(polygon => {
    polygon.setStyle({ fillOpacity: 0.25, weight: 2, opacity: 0.8 });
  });

  markers.forEach(m => {
    if (m.closePopup) m.closePopup();
  });
}

function focusTech(index) {
  const tech = sortedTechnicians.value[index];
  if (tech && map) {
    map.setView([tech.lat, tech.lon], 12);

    // Find and open popup for this tech
    markers.forEach(m => {
      if (m.getPopup && m.getPopup() && m.getPopup().getContent().includes(tech.name)) {
        m.openPopup();
      }
    });
  }
}

async function searchByAddress() {
  const input = addressInput.value.trim();
  if (!input) return;

  isSearching.value = true;
  searchStatus.value = 'loading';
  searchStatusText.value = 'Hledám adresu...';

  try {
    const geoRes = await fetch(`/api/mapy/geocode?query=${encodeURIComponent(input)}`);
    const geoData = await geoRes.json();

    if (!geoData.items || geoData.items.length === 0) {
      searchStatus.value = 'error';
      searchStatusText.value = 'Adresa nenalezena';
      isSearching.value = false;
      return;
    }

    const loc = geoData.items[0];
    currentLocation.value = { lat: loc.position.lat, lon: loc.position.lon, label: loc.name };

    searchStatusText.value = 'Počítám vzdálenosti...';
    await calculateDistances();

    isSearchMode.value = true;
    searchStatus.value = 'success';
    searchStatusText.value = '✓ Nalezeno: ' + currentLocation.value.label;

    updateMapMarkers();
  } catch (e) {
    console.error(e);
    searchStatus.value = 'error';
    searchStatusText.value = 'Chyba při vyhledávání';
  }

  isSearching.value = false;
}

async function calculateDistances() {
  for (let i = 0; i < technicians.value.length; i += 5) {
    const batch = technicians.value.slice(i, i + 5);
    await Promise.all(batch.map(async tech => {
      try {
        const res = await fetch(`/api/mapy/route?start=${tech.lon},${tech.lat}&end=${currentLocation.value.lon},${currentLocation.value.lat}&routeType=car_fast`);
        if (res.ok) {
          const data = await res.json();
          if (data.length !== undefined) {
            tech.distance = data.length / 1000;
            tech.duration = data.duration / 60;
            return;
          }
        }
      } catch (e) {
        // Fallback to haversine
      }
      tech.distance = haversineDistance(currentLocation.value.lat, currentLocation.value.lon, tech.lat, tech.lon);
      tech.duration = 0;
    }));
  }
}

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function clearSearch() {
  isSearchMode.value = false;
  currentLocation.value = null;
  technicians.value.forEach(t => {
    delete t.distance;
    delete t.duration;
  });
  addressInput.value = '';
  searchStatus.value = null;
  searchStatusText.value = '';
  updateMapMarkers();
}

async function initMap() {
  if (!mapContainer.value) return;

  map = L.map(mapContainer.value).setView([49.8, 15.5], 7);

  // Add Mapy.cz tiles with dark filter
  const tileLayer = L.tileLayer(`https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${MAPY_API_KEY}`, {
    minZoom: 0,
    maxZoom: 19,
    attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">© Seznam.cz a.s.</a>',
    className: 'dark-tiles'
  }).addTo(map);

  // Add Mapy.cz logo
  const LogoControl = L.Control.extend({
    options: { position: 'bottomleft' },
    onAdd: function () {
      const container = L.DomUtil.create('div');
      const link = L.DomUtil.create('a', '', container);
      link.href = 'http://mapy.cz/';
      link.target = '_blank';
      link.innerHTML = '<img src="https://api.mapy.cz/img/api/logo.svg" alt="Mapy.cz" />';
      return container;
    }
  });
  new LogoControl().addTo(map);

  loadRegionPolygons();
  updateMapMarkers();
}

function loadRegionPolygons() {

  // Simplified region boundaries for visualization
  const geoJsonToRegionId = {
    "Hlavní město Praha": "praha",
    "Středočeský kraj": "stredocesky",
    "Jihočeský kraj": "jihocesky",
    "Plzeňský kraj": "plzensky",
    "Karlovarský kraj": "karlovarsky",
    "Ústecký kraj": "ustecky",
    "Liberecký kraj": "liberecky",
    "Královéhradecký kraj": "kralovehradecky",
    "Pardubický kraj": "pardubicky",
    "Kraj Vysočina": "vysocina",
    "Jihomoravský kraj": "jihomoravsky",
    "Olomoucký kraj": "olomoucky",
    "Zlínský kraj": "zlinsky",
    "Moravskoslezský kraj": "moravskoslezsky"
  };

  // Note: Full GeoJSON would be loaded here, simplified for component size
  // For now, we'll just have markers without region polygons
}

function updateMapMarkers() {
  if (!map) return;

  // Clear existing markers and routes
  markers.forEach(m => map.removeLayer(m));
  markers = [];
  routeLines.forEach(l => map.removeLayer(l));
  routeLines = [];

  const techs = filteredTechnicians.value;
  const bounds = L.latLngBounds();

  // Add search location marker
  if (isSearchMode.value && currentLocation.value) {
    const searchIcon = L.divIcon({
      className: 'custom-div-icon',
      html: '<div style="font-size: 32px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));">📍</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 36]
    });
    const searchMarker = L.marker([currentLocation.value.lat, currentLocation.value.lon], { icon: searchIcon })
      .bindPopup(`<div class="popup-name" style="font-weight: 600; font-size: 14px; color: #fff;">Místo instalace</div><div class="popup-detail" style="font-size: 12px; color: #6b7280;">${currentLocation.value.label || ''}</div>`)
      .addTo(map);
    markers.push(searchMarker);
    bounds.extend([currentLocation.value.lat, currentLocation.value.lon]);
  }

  // Group technicians by location
  const groups = {};
  let sortedTechs = [...techs];
  if (isSearchMode.value && currentLocation.value) {
    sortedTechs.sort((a, b) => (a.distance || 9999) - (b.distance || 9999));
  }

  sortedTechs.forEach((tech, index) => {
    tech._markerNumber = index + 1;
    const key = `${tech.lat},${tech.lon}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(tech);
  });

  // Create markers for each group
  Object.values(groups).forEach(group => {
    const firstTech = group[0];
    const color = regionColors[firstTech.regionId] || '#888';
    const isSlovakia = firstTech.regionId === 'slovensko';
    const isTopResult = isSearchMode.value && group.some(t => t._markerNumber <= 3);

    const markerNumbers = group.map(t => t._markerNumber).join(',');

    const popupContent = group.map(tech => {
      const distInfo = tech.distance !== undefined
        ? `<div style="font-size: 12px; color: #22c55e; font-weight: 500; margin-top: 6px;">🚗 ${tech.distance.toFixed(1)} km${tech.duration ? ` • ⏱️ ${formatDuration(tech.duration)}` : ''}</div>`
        : '';
      return `<div style="font-weight: 600; font-size: 14px; color: #fff;">${tech.name}</div><div style="font-size: 12px; color: #6b7280;">📍 ${tech.city}, ${tech.region}</div><div style="font-size: 12px; color: #6b7280;">📞 ${tech.phone}</div><div style="font-size: 12px; color: #6b7280;">✉️ ${tech.email}</div>${distInfo}`;
    }).join('<hr style="border:none;border-top:1px solid rgba(255,255,255,0.2);margin:8px 0;">');

    const markerStyle = isTopResult
      ? `background: linear-gradient(135deg, #ff6b35, #ff3366); min-width: 32px; height: 32px; font-size: 14px;`
      : `background: ${color}; min-width: 28px; height: 28px;`;

    const icon = L.divIcon({
      className: 'custom-div-icon',
      html: `<div style="${markerStyle} padding: 0 6px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; color: ${isSlovakia ? '#000' : '#fff'}; border: 2px solid ${isSlovakia ? '#000' : '#fff'}; box-shadow: 0 2px 10px rgba(0,0,0,0.3); white-space: nowrap;">${markerNumbers}</div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });

    const marker = L.marker([firstTech.lat, firstTech.lon], { icon })
      .bindPopup(popupContent, {
        className: 'dark-popup'
      })
      .on('mouseover', function () {
        highlightTech(firstTech.name, firstTech.regionId);
        this.openPopup();
      })
      .on('mouseout', () => unhighlightRegion())
      .addTo(map);

    markers.push(marker);
    bounds.extend([firstTech.lat, firstTech.lon]);
  });

  // Draw routes for top 3 technicians
  if (isSearchMode.value && currentLocation.value) {
    drawRoutes(sortedTechs.slice(0, 3));
  }

  if (bounds.isValid()) {
    map.fitBounds(bounds, { padding: [50, 50] });
  }
}

async function drawRoutes(techs) {
  for (let i = 0; i < techs.length; i++) {
    const tech = techs[i];
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${tech.lon},${tech.lat};${currentLocation.value.lon},${currentLocation.value.lat}?overview=full&geometries=geojson`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.code === 'Ok' && data.routes && data.routes[0]) {
          const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          const line = L.polyline(coords, {
            color: '#ef4444',
            weight: i === 0 ? 5 : 3,
            opacity: i === 0 ? 0.9 : 0.6
          }).addTo(map);
          routeLines.push(line);
        }
      }
    } catch (e) {
      console.warn('Route error:', e);
    }
  }
}

function destroyMap() {
  if (map) {
    map.remove();
    map = null;
  }
  markers = [];
  routeLines = [];
  regionPolygons = {};
}

// Watchers
watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    initSelectedRegions();
    await nextTick();
    await initMap();
  } else {
    destroyMap();
  }
});

watch(filterInput, () => {
  updateMapMarkers();
});

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    initSelectedRegions();
    nextTick(() => initMap());
  }
});

onUnmounted(() => {
  destroyMap();
});
</script>

<style scoped>
/* Dark map tiles filter */
:deep(.dark-tiles) {
  filter: brightness(0.6) invert(1) contrast(3) hue-rotate(200deg) saturate(0.3) brightness(0.7);
}

/* Leaflet popup styling */
:deep(.leaflet-popup-content-wrapper) {
  background: #101018;
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

:deep(.leaflet-popup-tip) {
  background: #101018;
}

:deep(.leaflet-container) {
  background: #1a1a25;
}

/* Spin animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 0.8s linear infinite;
}
</style>
